import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import siteConfig from '../config/siteConfig.json';
import SEO from '../components/SEO';

const Home = () => {
  const { hero, business } = siteConfig;
  
  const heroImages = [
    '/gallery/art-wall-sign.jpg',
    '/gallery/treatment-room-orchid.jpg',
    '/gallery/incredible-mirror.jpg'
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 8000); // Change image every 8 seconds

    return () => clearInterval(interval);
  }, [heroImages.length]);

  // Structured Data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MedicalBusiness",
    "name": "ART Aesthetics & Skincare",
    "alternateName": "ART Aesthetics",
    "url": "https://artaestheticsskincare.com",
    "logo": "https://artaestheticsskincare.com/gallery/art-wall-sign.jpg",
    "image": "https://artaestheticsskincare.com/gallery/art-wall-sign.jpg",
    "description": "Professional aesthetic treatments and advanced skincare in Widnes, UK. Expert facials, microneedling, skin boosters, and personalized consultations by NHS Registered Nurse Aimee Turner.",
    "priceRange": "££",
    "email": "info@artaestheticsskincare.com",
    "hasMap": "https://maps.app.goo.gl/oLykmTW4paVJ7i4W9",
    "areaServed": ["Widnes", "Runcorn", "Warrington", "St Helens", "Liverpool"],
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Hughes Close",
      "addressLocality": "Widnes",
      "postalCode": "WA8 5EJ",
      "addressCountry": "GB"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "53.361389",
      "longitude": "-2.732778"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
        "opens": "09:00",
        "closes": "17:00"
      },
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Saturday"],
        "opens": "10:00",
        "closes": "14:00"
      }
    ],
    "sameAs": [
      "https://www.instagram.com/art_aesthetics_skin",
      "https://maps.app.goo.gl/oLykmTW4paVJ7i4W9"
    ],
    "founder": {
      "@type": "Person",
      "name": "Aimee Turner",
      "jobTitle": "NHS Registered Nurse & Aesthetic Therapist"
    }
  };

  return (
    <>
      <SEO
        title="Acne & Skincare Treatments in Widnes | ART Aesthetics & Skincare"
        description="ART Aesthetics & Skincare helps clients in Widnes and nearby towns improve acne, skin health, and confidence with personalised treatment plans."
        path="/"
        jsonLd={structuredData}
      />

      <section className="hero">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentImageIndex}
            className="hero-background"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.5 }}
            style={{
              backgroundImage: `url(${heroImages[currentImageIndex]})`
            }}
          />
        </AnimatePresence>
        <div className="hero-overlay"></div>
        <div className="hero-container">
          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.div
              className="location"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.6 }}
            >
              <span className="location-icon">{business.locationEmoji}</span>
              {hero.location}
            </motion.div>

            <motion.h1
              className="hero-title"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              {hero.mainTitle}
            </motion.h1>

            <motion.h2
              className="hero-subtitle"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.8 }}
            >
              {hero.subtitle}
            </motion.h2>

            <motion.div
              className="hero-buttons"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
            >
              <a 
                href="https://that-time.co.uk/art-aesthetics-skincare" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="cta-button primary"
              >
                Book Appointment
              </a>
              <Link to="/services" className="cta-button secondary">
                View Treatments
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Acne Specialization Callout */}
      <section className="acne-callout">
        <div className="callout-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="callout-content"
          >
            <span className="callout-badge">Acne Specialist</span>
            <h2>Struggling with Acne?</h2>
            <p>
              Acne can be exhausting — especially when it feels like nothing is working.
            </p>
            <p>
              At ART Aesthetics & Skincare, acne treatment isn't about harsh quick fixes. It's about understanding 
              inflammation, barrier health, hormones, and lifestyle — and building a plan that supports 
              your skin properly.
            </p>
            <p className="emphasis">You don't have to keep guessing anymore.</p>
            <Link to="/about" className="cta-button primary">
              Learn More About My Approach
            </Link>
            <Link to="/services/consultations" className="cta-button secondary">
              Start with a Skin Consultation
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default Home;
